import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { mergeMap, map, catchError } from 'rxjs/operators';
import * as LoadActions from '../actions/load.actions';
import { Store } from '@ngrx/store';
import { AppState } from '../../app.state';
import { SpotsService } from '../services/spots.service';
import { Load } from '../models/load.model';
import * as UiActions from '../../ui/actions/ui.actions';

@Injectable()
export class LoadEffects {
  constructor(
    private actions$: Actions,
    private spotsService: SpotsService,
    private store: Store<AppState>
  ) { }

  loadLoads$ = createEffect(() => this.actions$.pipe(
    ofType(LoadActions.loadLoads),
    mergeMap((action) => {
      const { organizationId, date } = action; // Extract organizationId and date from the action

      return this.spotsService.getLoads(organizationId, date).pipe(
        map((loads: Load[]) => {
          return LoadActions.loadLoadsSuccess({ loads });
        }),
        catchError(error => {
          return of(UiActions.serviceCallFailure({ action: LoadActions.loadLoads, error }))
        })
      )
    })
  ));

  loadLoad$ = createEffect(() => this.actions$.pipe(
    ofType(LoadActions.loadLoad),
    mergeMap(({ organizationId, loadId }) => this.spotsService.getLoadById(organizationId, loadId).pipe(
      map((load: Load) => {
        return LoadActions.loadLoadSuccess({ load });
      }),
      catchError(error => {
        return of(UiActions.serviceCallFailure({ action: LoadActions.loadLoad, error }))
      })
    ))
  ));

  addLoad$ = createEffect(() => this.actions$.pipe(
    ofType(LoadActions.addLoad),
    mergeMap(({ organizationId, load }) => this.spotsService.addLoad(organizationId, load).pipe(
      map((load: Load) => {
        return LoadActions.addLoadSuccess({ load });
      }),
      catchError(error => {
        return of(UiActions.serviceCallFailure({ action: LoadActions.addLoad, error }))
      })
    ))
  ));

  addLoadsBulk$ = createEffect(() => this.actions$.pipe(
    ofType(LoadActions.addLoadsBulk),
    mergeMap(({ organizationId, loads }) => this.spotsService.addLoadsBulk(organizationId, loads).pipe(
      map((createdLoads: Load[]) => {
        return LoadActions.addLoadsBulkSuccess({ loads: createdLoads });
      }),
      catchError(error => {
        return of(UiActions.serviceCallFailure({ action: LoadActions.addLoadsBulk, error }))
      })
    ))
  ));

  updateLoad$ = createEffect(() => this.actions$.pipe(
    ofType(LoadActions.updateLoad),
    mergeMap(({ organizationId, loadId, load }) => this.spotsService.updateLoad(organizationId, loadId, load).pipe(
      map((load: Load) => {
        return LoadActions.updateLoadSuccess({ load });
      }),
      catchError(error => {
        return of(UiActions.serviceCallFailure({ action: LoadActions.updateLoad, error }))
      })
    ))
  ));

  deleteLoad$ = createEffect(() => this.actions$.pipe(
    ofType(LoadActions.deleteLoad),
    mergeMap(({ organizationId, loadId }) => this.spotsService.deleteLoad(organizationId, loadId).pipe(
      map(() => {
        return LoadActions.deleteLoadSuccess({ loadId });
      }),
      catchError(error => {
        return of(UiActions.serviceCallFailure({ action: LoadActions.deleteLoad, error }))
      })
    ))
  ));
}
